import React from "react";
import { useOutletContext, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import CustomBlocks from "@/components/site/CustomBlocks";

export default function HowItWorks({ previewSite }) {
  const outlet = useOutletContext();
  const site = previewSite || outlet?.site;
  if (!site) return null;
  const p = site.pages.how_it_works;
  return (
    <div data-testid="how-it-works-page">
      <section className="max-w-4xl mx-auto px-6 md:px-10 pt-20 pb-16">
        <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#9A8C73] mb-5">{p.hero.eyebrow}</p>
        <h1 className="font-display text-5xl md:text-6xl font-semibold tracking-tight text-[#1A201D] mb-6">{p.hero.title}</h1>
        <p className="text-lg text-[#4A5550] leading-relaxed max-w-2xl">{p.hero.subtitle}</p>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-24">
        <ol className="relative border-l border-[#E5DFD3] ml-4">
          {p.steps.map((s, i) => (
            <motion.li key={i} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="mb-14 ml-10 relative">
              <span className="absolute -left-[3.35rem] top-0 w-10 h-10 rounded-full bg-[#2C3531] text-[#FDFBF7] font-bold text-sm flex items-center justify-center">{s.num}</span>
              <h3 className="font-display text-2xl md:text-3xl font-semibold text-[#1A201D] mb-2">{s.title}</h3>
              <p className="text-[#4A5550] leading-relaxed">{s.desc}</p>
            </motion.li>
          ))}
        </ol>
      </section>

      <section className="bg-[#F3EFE7] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#1A201D] mb-2">{p.matrix.title}</h2>
          {p.matrix.subtitle && <p className="text-[#4A5550] mb-10">{p.matrix.subtitle}</p>}
          <div className="overflow-x-auto rounded-2xl border border-[#E5DFD3] bg-[#FDFBF7]">
            <table className="w-full min-w-[720px] text-left" data-testid="opportunity-matrix">
              <thead>
                <tr className="border-b border-[#E5DFD3] text-xs uppercase tracking-wider text-[#9A8C73]">
                  <th className="py-4 px-6 font-bold">Service Category</th>
                  <th className="py-4 px-6 font-bold">Equipment Footprint</th>
                  <th className="py-4 px-6 font-bold">Avg. Value</th>
                  <th className="py-4 px-6 font-bold">Ideal Partners</th>
                  <th className="py-4 px-6 font-bold bg-[#2C3531]/[0.04]">Host Markup</th>
                </tr>
              </thead>
              <tbody>
                {p.matrix.rows.map((r, i) => (
                  <tr key={i} className="border-b border-[#E5DFD3] last:border-0 text-sm">
                    <td className="py-4 px-6 font-semibold text-[#1A201D]">{r.category}</td>
                    <td className="py-4 px-6 text-[#4A5550]">{r.footprint}</td>
                    <td className="py-4 px-6 text-[#4A5550]">{r.value}</td>
                    <td className="py-4 px-6 text-[#4A5550]">{r.partners}</td>
                    <td className="py-4 px-6 font-semibold text-[#2C3531] bg-[#2C3531]/[0.04]">{r.markup}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-12">
            <Link to="/register" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#2C3531] text-[#FDFBF7] font-semibold hover:scale-105 transition-transform duration-300">
              Start Your Free Trial <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
      <CustomBlocks blocks={p.blocks} />
    </div>
  );
}
